import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTO_LOAD_MAX_TIMES = 3;
const AUTO_LOAD_ROOT_MARGIN = '0px 0px 400px 0px';

/**
 * 목록 끝이 가까워지면 다음 쪽을 자동으로 부른다. 같은 목록(listKey)에서 maxTimes번까지만 하고,
 * 그다음은 '더 보기' 버튼으로 넘긴다(끝없는 스크롤로 푸터·위치를 잃지 않게).
 * IntersectionObserver가 없는 환경은 처음부터 버튼만 쓴다.
 *
 * page가 바뀔 때마다 관찰을 새로 시작한다. 새 관찰은 시작하자마자 한 번 보고하므로,
 * 불러온 뒤에도 끝이 여전히 보이면(화면이 큰 경우) 이어서 부른다.
 */
export default function useAutoLoadMore({
  enabled,
  listKey,
  page,
  onLoadMore,
  maxTimes = AUTO_LOAD_MAX_TIMES,
}) {
  const [node, setNode] = useState(null);
  const countRef = useRef({ key: listKey, count: 0 });
  if (countRef.current.key !== listKey) countRef.current = { key: listKey, count: 0 };
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const supported = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
  const exhausted = !supported || countRef.current.count >= maxTimes;

  useEffect(() => {
    if (!enabled || exhausted || !node) return undefined;
    let fired = false;
    const observer = new window.IntersectionObserver((entries) => {
      if (fired || !entries.some((entry) => entry.isIntersecting)) return;
      fired = true;
      countRef.current.count += 1;
      observer.disconnect();
      onLoadMoreRef.current();
    }, { rootMargin: AUTO_LOAD_ROOT_MARGIN });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, exhausted, node, listKey, page]);

  const sentinelRef = useCallback((element) => setNode(element), []);

  return { sentinelRef, autoLoadExhausted: exhausted };
}
